import WorkspaceDialog from '../WorkspaceDialog';
import { useTenantBootstrap } from '@tark/shared/hooks';
import { getPrivateQueryGeneration } from '@tark/shared/lib/queryPrivacy';
import { CalendarClock, Loader2, MapPin } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@tark/shared/components';
import { useTenantFamilyBookableSlots, useTenantFamilySelfBooking } from '@tark/shared/hooks';
import { useEffect, useRef, useState } from 'react';
import { bookableSlotTime } from '../../lib/bookableSlotTime';

export function FamilyBookableOpeningsPanel({ athleteId, athleteName }: { athleteId: number | null; athleteName?: string }) {
  const query = useTenantFamilyBookableSlots(athleteId, athleteId != null);
  const booking = useTenantFamilySelfBooking();
  const [visibleCount, setVisibleCount] = useState(16);
  type Slot = NonNullable<typeof query.data>['slots'][number];
  const [selected, setSelected] = useState<Slot | null>(null);
  const [reviewOwner,setReviewOwner] = useState<{athleteId:number;tenantId:number|undefined;generation:number;name:string;studio:string} | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [confirmed, setConfirmed] = useState(false);
  const { activeTenant } = useTenantBootstrap();
  const current = useRef({ athleteId, tenantId: activeTenant?.tenant?.id });
  current.current = { athleteId, tenantId: activeTenant?.tenant?.id };
  const mounted = useRef(true);
  useEffect(() => { mounted.current = true; return () => { mounted.current = false; }; }, []);
  useEffect(() => { setSelected(null); setReviewOwner(null); setNotice(null); setConfirmed(false); }, [athleteId, activeTenant?.tenant?.id]);
  const confirmBooking = async () => {
    if (!selected || !athleteId || !reviewOwner || booking.isPending || reviewOwner.generation !== getPrivateQueryGeneration() || reviewOwner.athleteId !== athleteId || reviewOwner.tenantId !== current.current.tenantId) return;
    const scope = { ...current.current, generation: getPrivateQueryGeneration() };
    const isCurrent = () => mounted.current && scope.generation === getPrivateQueryGeneration() && scope.athleteId === current.current.athleteId && scope.tenantId === current.current.tenantId;
    try {
      const result = await booking.mutateAsync({ athleteId, offeringId:selected.offeringId, startsAt:selected.startsAt, paymentPolicy:selected.paymentPolicy, assertCurrent: () => { if (!isCurrent()) throw new Error('Family selection changed'); } });
      if (!isCurrent()) return;
      if (result.hold.status !== 'BOOKED') { setNotice('Check your schedule before trying this booking again.'); return; }
      setConfirmed(true);
    } catch {
      if (isCurrent()) setNotice('Could not confirm the result. Check your schedule before booking again.');
    }
  };
  useEffect(() => setVisibleCount(16), [athleteId]);
  if (!athleteId) return null;

  const groups = new Map<string, NonNullable<typeof query.data>['slots']>();
  const outsideSlots = (query.data?.slots ?? []).filter((slot) => slot.paymentPolicy === 'OFF_PLATFORM');
  outsideSlots.sort((a, b) => Date.parse(a.startsAt) - Date.parse(b.startsAt));
  outsideSlots.slice(0, visibleCount).forEach((slot) => {
    const { key } = bookableSlotTime(slot.startsAt, slot.timezone || query.data?.timezone);
    groups.set(key, [...(groups.get(key) ?? []), slot]);
  });

  return (
    <Card variant="default">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <CalendarClock className="h-5 w-5 text-primary" />
          Open lesson times
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {query.isLoading ? (
          <div className="flex items-center gap-2 text-sm text-text-muted"><Loader2 className="h-4 w-4 animate-spin" /> Loading open times</div>
        ) : query.isError ? (
          <button type="button" className="text-sm font-semibold text-primary" onClick={() => query.refetch()}>Could not load. Retry</button>
        ) : outsideSlots.length === 0 ? (
          <p className="text-sm text-text-muted">No open times right now.</p>
        ) : Array.from(groups.entries()).map(([key, slots]) => (
          <div key={key} className="space-y-2">
            <p className="text-xs font-semibold uppercase tracking-[0.12em] text-text-subtle">
              <span>{bookableSlotTime(slots[0].startsAt, slots[0].timezone || query.data?.timezone).day}</span>
              {' · '}<span>{bookableSlotTime(slots[0].startsAt, slots[0].timezone || query.data?.timezone).zoneLabel}</span>
            </p>
            {slots.map((slot) => (
              <div key={`${slot.offeringId}-${slot.startsAt}`} className="flex min-h-14 items-center gap-3 rounded-lg border border-border px-3 py-2">
                <span className="min-w-20 rounded-lg bg-primary/10 px-2 py-2 text-center text-xs font-bold text-primary">
                  {bookableSlotTime(slot.startsAt, slot.timezone || query.data?.timezone).time}
                </span>
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-semibold text-text">{slot.offeringTitle}</p>
                  <p className="mt-1 flex items-center gap-1 text-xs font-semibold text-primary"><MapPin className="h-3 w-3" /> In-person</p>
                  <p className="mt-1 flex items-center gap-1 truncate text-xs text-text-muted">
                    {slot.locationLabel ? <MapPin className="h-3 w-3 shrink-0" /> : null}
                    {slot.locationLabel || `${Math.round((new Date(slot.endsAt).getTime() - new Date(slot.startsAt).getTime()) / 60000)} min`}
                    {slot.priceAmount == null ? '' : ` · $${Number(slot.priceAmount).toFixed(2)}`}
                  </p>
                </div>
                <button
                  type="button"
                  className="inline-flex h-11 items-center justify-center rounded-lg bg-primary px-3 text-sm font-semibold [color:var(--color-on-primary)] disabled:opacity-60"
                  disabled={booking.isPending}
                  onClick={() => { setNotice(null); setConfirmed(false); setReviewOwner({athleteId, tenantId:activeTenant?.tenant?.id,generation:getPrivateQueryGeneration(),name:athleteName || 'Selected athlete',studio:activeTenant?.tenant?.displayName || 'Your studio'}); setSelected(slot); }}
                >
                  Book
                </button>
              </div>
            ))}
          </div>
        ))}
        {selected && reviewOwner && reviewOwner.athleteId === athleteId && reviewOwner.tenantId === activeTenant?.tenant?.id && reviewOwner.generation === getPrivateQueryGeneration() ? <WorkspaceDialog title={confirmed ? 'Lesson booked' : 'Review booking'} busy={booking.isPending} onClose={() => setSelected(null)}>
          <div className="my-5 space-y-3">
            <p className="text-xl font-semibold text-text">{selected.offeringTitle}</p>
            <p className="text-sm font-semibold text-text">For {reviewOwner.name}</p>
            <p className="text-sm text-text-muted">{reviewOwner.studio}</p>
            <p className="text-sm text-text-muted">{bookableSlotTime(selected.startsAt, selected.timezone || query.data?.timezone).day} · {bookableSlotTime(selected.startsAt, selected.timezone || query.data?.timezone).time} {bookableSlotTime(selected.startsAt, selected.timezone || query.data?.timezone).zoneLabel}</p>
            <p className="text-sm text-text-muted">{selected.locationLabel || 'In person'} · {Math.round((Date.parse(selected.endsAt)-Date.parse(selected.startsAt))/60000)} min</p>
            <p className="text-lg font-bold text-primary">${Number(selected.priceAmount).toFixed(2)} <span className="text-xs font-normal text-text-muted">· Pay outside TARK</span></p>
            {confirmed ? <p role="status" className="rounded-lg bg-primary/10 p-3 text-sm text-text">Added to your family schedule.</p> : null}
            {notice ? <p role="alert" className="text-sm text-text-muted">{notice}</p> : null}
          </div>
          <div className="flex flex-wrap justify-end gap-3">
            <button type="button" className="min-h-11 px-4 font-semibold text-text-muted" disabled={booking.isPending} onClick={() => setSelected(null)}>{confirmed || notice ? 'Back to schedule' : 'Cancel'}</button>
            {!confirmed && !notice ? <button type="button" className="min-h-11 rounded-lg bg-primary px-5 font-semibold [color:var(--color-on-primary)] disabled:opacity-60" disabled={booking.isPending || selected.priceAmount == null || Number(selected.priceAmount) <= 0} onClick={() => void confirmBooking()}>{booking.isPending?'Booking…':'Confirm booking'}</button> : null}
          </div>
        </WorkspaceDialog> : null}
        {outsideSlots.length > visibleCount ? (
          <button type="button" className="min-h-11 text-sm font-semibold text-primary" onClick={() => setVisibleCount((count) => count + 16)}>
            Show more times ({outsideSlots.length - visibleCount} remaining)
          </button>
        ) : null}
      </CardContent>
    </Card>
  );
}
